package de.xtl.retroguide.question.scenes

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.protocol.AnswerCategory

private val Backdrop = listOf(Color(0xFFF3E5F5), Color(0xFFE8EAF6))

private val AnswerCategory.faceColor: Color
    get() = when (this) {
        AnswerCategory.MAD -> Color(0xFFEF5350)
        AnswerCategory.SAD -> Color(0xFF5C6BC0)
        else -> Color(0xFFFFCA28)
    }

/** Three faces whose size follows their share of the answers; Mad frowns, Sad cries, Glad smiles. */
@Composable
internal fun MoodFacesScene(counts: Map<AnswerCategory, Int>, modifier: Modifier = Modifier) {
    val moods = listOf(AnswerCategory.MAD, AnswerCategory.SAD, AnswerCategory.GLAD)
    val total = moods.sumOf { counts[it] ?: 0 }.coerceAtLeast(1)
    val scales = moods.map { mood ->
        val target = .55f + .45f * (counts[mood] ?: 0) / total
        val scale = remember { Animatable(0f) }
        LaunchedEffect(target) {
            scale.animateTo(target, spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessLow))
        }
        scale
    }
    val transition = rememberInfiniteTransition()
    val time by transition.animateFloat(
        initialValue = 0f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(tween(4_000, easing = LinearEasing)),
    )
    Box(modifier.fillMaxWidth().aspectRatio(16f / 9f).clip(RoundedCornerShape(24.dp))) {
        Canvas(Modifier.fillMaxSize()) {
            drawRect(Brush.verticalGradient(Backdrop))
            moods.forEachIndexed { index, mood ->
                val center = Offset(size.width * (.2f + index * .3f), size.height * .44f)
                val radius = size.height * .26f * scales[index].value
                val blinking = ((time + index * .27f) % 1f) in .9f..0.95f
                drawFace(mood, center, radius, blinking, time)
            }
        }
        Row(Modifier.align(Alignment.BottomCenter).fillMaxWidth().padding(bottom = 10.dp)) {
            moods.forEach { mood ->
                Box(Modifier.weight(1f), contentAlignment = Alignment.Center) {
                    SceneBadge(mood, counts[mood] ?: 0)
                }
            }
        }
    }
}

private fun DrawScope.drawFace(mood: AnswerCategory, center: Offset, radius: Float, blinking: Boolean, time: Float) {
    if (radius <= 0f) return
    val ink = Color(0xFF3E2723)
    val stroke = radius * .07f
    drawCircle(Color.Black.copy(alpha = .08f), radius, center + Offset(0f, radius * .08f))
    drawCircle(
        Brush.radialGradient(listOf(mood.faceColor.copy(alpha = .75f), mood.faceColor), center - Offset(radius * .3f, radius * .3f), radius * 1.6f),
        radius,
        center,
    )
    val eyeY = center.y - radius * .18f
    listOf(-1f, 1f).forEach { side ->
        val eye = Offset(center.x + side * radius * .33f, eyeY)
        if (blinking) {
            drawLine(ink, eye - Offset(radius * .1f, 0f), eye + Offset(radius * .1f, 0f), strokeWidth = stroke, cap = StrokeCap.Round)
        } else {
            drawOval(ink, eye - Offset(radius * .07f, radius * .1f), Size(radius * .14f, radius * .2f))
        }
        when (mood) {
            AnswerCategory.MAD -> drawLine(
                ink,
                Offset(center.x + side * radius * .5f, eyeY - radius * .28f),
                Offset(center.x + side * radius * .14f, eyeY - radius * .14f),
                strokeWidth = stroke,
                cap = StrokeCap.Round,
            )
            AnswerCategory.SAD -> drawLine(
                ink,
                Offset(center.x + side * radius * .48f, eyeY - radius * .16f),
                Offset(center.x + side * radius * .16f, eyeY - radius * .3f),
                strokeWidth = stroke,
                cap = StrokeCap.Round,
            )
            else -> drawCircle(Color(0xFFFF8A80).copy(alpha = .6f), radius * .12f, Offset(center.x + side * radius * .55f, center.y + radius * .12f))
        }
    }
    val mouth = Size(radius * .8f, radius * .55f)
    when (mood) {
        AnswerCategory.GLAD -> drawArc(
            ink, 15f, 150f, false,
            topLeft = Offset(center.x - mouth.width / 2, center.y - mouth.height * .35f),
            size = mouth,
            style = Stroke(stroke, cap = StrokeCap.Round),
        )
        AnswerCategory.SAD -> {
            drawArc(
                ink, 200f, 140f, false,
                topLeft = Offset(center.x - mouth.width / 2, center.y + radius * .3f),
                size = mouth,
                style = Stroke(stroke, cap = StrokeCap.Round),
            )
            val fall = (time * 2f) % 1f
            val tear = Offset(center.x + radius * .33f, eyeY + radius * (.15f + fall * .5f))
            drawCircle(Color(0xFF81D4FA).copy(alpha = 1f - fall), radius * .07f, tear)
        }
        else -> drawLine(
            ink,
            Offset(center.x - radius * .3f, center.y + radius * .45f),
            Offset(center.x + radius * .3f, center.y + radius * .38f),
            strokeWidth = stroke,
            cap = StrokeCap.Round,
        )
    }
}
